import { NumberFormatConfig } from '../../../utils/numberScale'
import { VariableType } from '../../../../components/Table/types'

interface GridRowData {
  style?: { decimals?: number }
  variableType?: VariableType
}

interface GridColDef {
  cellEditorParams?: { decimals?: number }
}

export interface GridFormatParams {
  data?: GridRowData
  colDef?: GridColDef
}

export interface GridNumberFormat {
  numberFormat: NumberFormatConfig
  isPercentage: boolean
}

const DEFAULT_DECIMALS: Record<VariableType, number> = {
  absolute: 0,
  percentage: 1,
  ratio: 1
}

/**
 * Build NumberFormatConfig from grid params (variable-based grids: driverData, allocation).
 *
 * Priority:
 * 1. row.style.decimals (highest — from formula/variable config)
 * 2. column.cellEditorParams.decimals
 * 3. Type-based default (absolute: 0, percentage: 1, ratio: 1)
 */
export const buildGridNumberFormat = (params: GridFormatParams, variableType?: VariableType): GridNumberFormat => {
  const type = variableType || params.data?.variableType || 'absolute'

  const decimals = params.data?.style?.decimals ?? params.colDef?.cellEditorParams?.decimals ?? DEFAULT_DECIMALS[type]

  return {
    numberFormat: { decimals },
    isPercentage: type === 'percentage'
  }
}

/**
 * Get decimals from GridNumberFormat.
 * Convenience for parsers and clipboard utils that need just the number.
 */
export const getDecimalsFromFormat = (format: GridNumberFormat): number => {
  return format.numberFormat.decimals ?? 0
}

/**
 * Shortcut: get decimals from grid params.
 * Replaces old getDecimalsFromParams — same priority logic, backed by buildGridNumberFormat.
 */
export const getDecimalsFromParams = (params: GridFormatParams, variableType?: VariableType): number => {
  return getDecimalsFromFormat(buildGridNumberFormat(params, variableType))
}
